import { useMemo } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import {
    useLoaderData,
    useNavigation,
    useSearchParams,
} from '@remix-run/react';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { PermissionEnum } from 'generated/graphql';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { getBMPagePath } from '~/modules/root/util/get-bm-page-path';
import { useEditPermissions, usePermissions } from '~/modules/user';
import AboutProfessional from '@modules/project/component/about-professional/AboutProfessional';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { DetailsPageRelatedTileGrid } from '~/ui-rocco/pages/details-page/DetailsPageRelatedTileGrid';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';

export const SHOW_ALL_PARAM_FEATURED_PRODUCTS = 'featured-products-show-all';
export const SHOW_ALL_PARAM_FEATURED_PROFESSIONALS =
    'featured-professionals-show-all';

const ProjectPageContent = () => {
    const { desktop } = useAppDisplayModeContext();
    const {
        project,
        featuredProducts,
        featuredProfessionals,
        featuredProjects,
    } = useLoaderData<typeof loader>();

    const navigation = useNavigation();
    const [searchParams] = useSearchParams();
    const { canEditProfile } = useEditPermissions(project.Professional.ID);
    const canCopyLink = usePermissions(PermissionEnum.BmMarketingView);
    const projectEditPath = getBMPagePath(
        project.Professional.URLSegment,
        'projects',
        project.URLSegment
    );

    const nextSearchParams = new URLSearchParams(navigation.location?.search);
    const showingAllFeaturedProducts = !!searchParams.get(
        SHOW_ALL_PARAM_FEATURED_PRODUCTS
    );
    const showingAllFeaturedProfessionals = !!searchParams.get(
        SHOW_ALL_PARAM_FEATURED_PROFESSIONALS
    );
    const featuredShowAllFeaturedProducts =
        nextSearchParams.get(SHOW_ALL_PARAM_FEATURED_PRODUCTS) === '1';
    const featuredShowAllFeaturedProfessionals =
        nextSearchParams.get(SHOW_ALL_PARAM_FEATURED_PROFESSIONALS) === '1';

    const isLoadingFeaturedProducts =
        navigation.state === 'loading' && featuredShowAllFeaturedProducts;
    const isLoadingFeaturedProfessionals =
        navigation.state === 'loading' && featuredShowAllFeaturedProfessionals;

    const featuredProductsList: TileViewModel<ProductTileViewModel>[] = useMemo(
        () =>
            featuredProducts.FeaturedProducts.products.map((product) => ({
                model: product,
                aspectRatio: 'square',
                section: 'ProjectUsedProducts',
            })),
        [featuredProducts.FeaturedProducts.products]
    );
    const featuredProfessionalsList: TileViewModel<ProfessionalTileViewModel>[] =
        useMemo(
            () =>
                featuredProfessionals.FeaturedProfessionals.professionals.map(
                    (professional) => ({
                        model: professional,
                        aspectRatio: 'default',
                        section: 'ProjectUsedProfessionals',
                    })
                ),
            [featuredProfessionals.FeaturedProfessionals.professionals]
        );
    const featuredProjectsList: TileViewModel<ProjectTileViewModel>[] = useMemo(
        () =>
            featuredProjects.FeaturedProjects.projects.map((project) => ({
                model: project,
                aspectRatio: 'default',
                section: 'ProjectRelated',
            })),
        [featuredProjects.FeaturedProjects.projects]
    );
    const featuredProductsAdmin = useTilesAdminData(featuredProductsList);
    const featuredProfessionalsAdmin = useTilesAdminData(
        featuredProfessionalsList
    );
    const featuredProjectsAdmin = useTilesAdminData(featuredProjectsList);

    return (
        <div className="flex flex-col gap-y-12 px-4 md:gap-y-32 md:px-0">
            {/* Featured Products */}
            <DetailsPageRelatedTileGrid
                type="products"
                title={`Products used in ${project.Title}`}
                tiles={featuredProductsAdmin}
                cta={
                    showingAllFeaturedProducts ||
                    featuredProducts.FeaturedProducts.pageInfo.totalCount <=
                        (desktop ? 5 : 4)
                        ? undefined
                        : {
                              type: 'link',
                              to: `?${SHOW_ALL_PARAM_FEATURED_PRODUCTS}=1`,
                              label: 'View all',
                              loading: isLoadingFeaturedProducts
                                  ? true
                                  : undefined,
                              preventScrollReset: true,
                          }
                }
                displayCount={{
                    desktop: 10,
                    mobile: 4,
                }}
                appDisplayMode={desktop ? 'desktop' : 'mobile'}
                noSlice={showingAllFeaturedProducts}
            />

            {/* Featured Professionals */}
            <DetailsPageRelatedTileGrid
                type="professionals"
                title={`Professionals used in ${project.Title}`}
                tiles={featuredProfessionalsAdmin}
                appDisplayMode={desktop ? 'desktop' : 'mobile'}
                cta={
                    showingAllFeaturedProfessionals ||
                    featuredProfessionals.FeaturedProfessionals.pageInfo
                        .totalCount <= 4
                        ? undefined
                        : {
                              type: 'link',
                              to: `?${SHOW_ALL_PARAM_FEATURED_PROFESSIONALS}=1`,
                              label: 'View all',
                              loading: isLoadingFeaturedProfessionals
                                  ? true
                                  : undefined,
                              preventScrollReset: true,
                          }
                }
                displayCount={{
                    desktop: 4,
                    mobile: 4,
                }}
                noSlice={showingAllFeaturedProfessionals}
            />

            {/* Featured Projects */}

            <DetailsPageRelatedTileGrid
                type="projects"
                title={`More projects by ${project.Professional.Title}`}
                tiles={featuredProjectsAdmin}
                appDisplayMode={desktop ? 'desktop' : 'mobile'}
                cta={
                    featuredProjects.FeaturedProjects.pageInfo.totalCount <= 4
                        ? undefined
                        : {
                              type: 'link',
                              to: `${project.Professional.Link}/case-studies#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                              label: 'View more',
                          }
                }
                displayCount={{
                    desktop: 4,
                    mobile: 4,
                }}
            />

            {desktop && (
                <>
                    <AboutProfessional data={project} />
                    <DetailPageButtons
                        id={project.ID}
                        editLink={projectEditPath}
                        shareableLink={project.Link}
                        canEdit={canEditProfile}
                        canCopyLink={canCopyLink}
                    />
                </>
            )}
        </div>
    );
};
export default ProjectPageContent;
